import { WidgetProgress } from '@agilize/components-core';
import { animaFadeOut, DownFadeIn, moveUp, shake } from '@styles/objects/animations.styles';
import { device } from '@styles/tools/breakpoints.styles';
import { headerHeightDesktop, headerHeightMobile } from '@styles/trumps/utilities.styles';
import styled, { css } from 'styled-components';

export const circleWidth = '48px';
export const liPadding = '28px';

export const StyledUl = styled.ul`
  margin: 0;
  list-style-type: none;
  position: relative;
  cursor: inherit;

  li:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: calc(24px + ${liPadding});
    bottom: calc(${liPadding} + 20px);
    height: calc(100% - ${circleWidth} - ${liPadding});
    width: 1px;
    background-color: ${props => props.theme.colors.grey200};
    z-index: 0;

    ${device.mobile} {
      left: 24px;
    }
  }
`;

export const OnboardingContainer = styled.div`
  max-height: calc(100vh - ${headerHeightDesktop});
  min-height: calc(100vh - ${headerHeightDesktop});
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  background: linear-gradient(
    to bottom,
    ${props => props.theme.colors.purple700} calc(${props => props.bgHeight || '40%'} - ${headerHeightDesktop}),
    ${props => props.theme.colors.grey100} calc(${props => props.bgHeight || '40%'} - ${headerHeightDesktop})
  );

  ${device.mobile} {
    max-height: calc(100vh - ${headerHeightMobile});
    min-height: calc(100vh - ${headerHeightMobile});
    background: linear-gradient(
      to bottom,
      ${props => props.theme.colors.purple700} calc(${props => props.bgHeight || '40%'} - ${headerHeightMobile}),
      ${props => props.theme.colors.grey100} calc(${props => props.bgHeight || '40%'} - ${headerHeightMobile})
    );
  }
`;

export const OnboardingHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

export const OnboardingH1 = styled.h1`
  display: flex;
  align-items: center;
  text-align: center;
  font-weight: 600;
  font-size: 2.4rem;
  color: ${props => props.theme.colors.white};
  margin-bottom: ${props => props.theme.global.gutterSpace * 1.5}px;
  padding-top: ${props => props.theme.global.gutterSpace}px;

  ${device.mobile} {
    padding-top: ${props => props.theme.global.gutterSpace * 0.75}px;
  }
`;

export const OnboardingH2 = styled.h2`
  display: flex;
  align-items: center;
  text-align: center;
  max-width: 588px;
  margin: 0;
  font-size: ${props => props.theme.fontSizes[3]};
  font-weight: 400;
  color: ${props => props.theme.colors.white};
  padding-bottom: ${props => props.theme.global.gutterSpace * 3}px;

  ${device.mobile} {
    padding: 0 ${props => props.theme.global.gutterSpace * 0.75}px ${props => props.theme.global.gutterSpace * 2}px
      ${props => props.theme.global.gutterSpace * 2}px;
  }
`;

export const OnboardingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  width: 100%;

  ${device.mobile} {
    flex: 1;
    justify-content: flex-start;
  }
`;

export const CardStyles = css`
  display: flex;
  flex-direction: column;
  min-height: 200px;
  width: 384px;
  background-color: ${props => props.theme.colors.white};
  border-radius: ${props => props.theme.forms.borderRadius * 2}px;
`;

export const CardAnimation = css`
  opacity: 0;
  animation: ${DownFadeIn} 0.3s ease-in-out 1 forwards;
`;

export const OnboardingContainerCard = styled.div`
  margin-left: ${props => props.theme.global.gutterSpace * 1.5}px;

  ${device.mobile} {
    margin-left: 0;
  }
`;

export const OnboardingCard = styled.div`
  ${CardStyles}
  flex: 1;
  width: 588px;
  padding: ${props => (props.smallPadding ? 12 : 40)}px;
  box-shadow: 0px 20px 56px -20px rgba(0, 0, 0, 0.1);
  transition: width 150ms, height 150ms linear;
  height: fit-content;

  ${props =>
    props.maxWidth &&
    css`
      max-width: ${props.maxWidth};
    `}
  ${props =>
    props.padding &&
    css`
      padding: ${props.padding};
    `}

  ${device.mobile} {
    width: 100%;
    max-width: 100%;
    min-height: calc(100vh - 66px);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: ${props => props.theme.global.gutterSpace}px ${props => props.theme.global.gutterSpace}px 0 ${props => props.theme.global.gutterSpace}px;
    margin-bottom: ${props => props.theme.global.gutterSpace * 0.75}px;

    form {
      height: 100%;
    }
  }
`;

export const FooterContainer = styled.div`
  width: 100%;
  height: 64px;
`;

export const ButtonsContainer = styled.div`
  margin-top: ${props => props.theme.global.gutterSpace * 2.5}px;

  ${device.mobile} {
    margin-top: 0;
    width: 100%;
    position: fixed;
    bottom: 0px;
    left: 0px;
    right: 0px;
    margin-bottom: 0px;
    background: ${props => props.theme.colors.white};
    z-index: 3;
  }
`;

export const ButtonsContent = styled.div`
  display: flex;
  justify-content: space-between;

  ${device.mobile} {
    padding: ${props => props.theme.global.gutterSpace}px;
  }
`;

export const FormsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  position: relative;
  margin-bottom: ${props => props.theme.global.gutterSpace * 2}px;

  ${device.mobile} {
    justify-content: flex-start;

    margin-bottom: 0;
    min-height: calc(100vh - ${headerHeightMobile});
  }
`;

export const WidgetContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const StyledWidgetProgress = styled(WidgetProgress)`
  ${device.mobile} {
    padding: ${props => props.theme.global.gutterSpace}px ${props => props.theme.global.gutterSpace / 2}px 0;
  }
`;

export const IconContainer = styled.div`
  padding: ${props => props.theme.global.gutterSpace * 1.25}px;

  :hover {
    .rocket {
      animation: ${shake} 1s linear, ${moveUp} 1.2s linear 1s;
    }
    .clouds {
      animation: ${animaFadeOut} 1.1s linear 1.1s;
    }
  }

  ${device.mobile} {
    display: none;
  }
`;

export const TextHelperContainer = styled.div`
  padding: ${props => props.theme.global.gutterSpace * 1.5}px;
`;

export const TitleHelperText = styled.h3`
  margin-bottom: ${props => props.theme.global.gutterSpace}px;
  font-size: ${props => props.theme.fontSizes[3]};
  font-weight: 600;
  color: ${props => props.theme.colors.grey900};
`;

export const DescriptionHelperText = styled.div`
  margin-bottom: ${props => props.theme.global.gutterSpace}px;
  font-size: ${props => props.theme.fontSizes[3]};
  color: ${props => props.theme.colors.grey700};
`;

export const HelperLink = styled.a`
  font-weight: 500;
  font-size: ${props => props.theme.fontSizes[3]};
  color: ${props => props.theme.colors.blue700};
`;

export const SectionTitle = styled.h2`
  font-weight: 600;
  font-size: ${props => props.theme.fontSizes[3]};
  color: ${props => props.theme.colors.grey900};
  margin: 0;
  text-align: ${props => (props.align ? props.align : 'left')};
`;

export const SectionSubTitle = styled.p`
  margin-top: ${props => props.mt ?? props.theme.global.gutterSpace}px;
  font-size: ${props => props.theme.fontSizes[3]};
  color: ${props => props.theme.colors.grey700};
  margin-bottom: 0;
  text-align: ${props => (props.align ? props.align : 'left')};
`;

export const StyledHr = styled.hr`
  margin: ${props => props.theme.global.gutterSpace * 2.5}px 0;
  display: block;
  height: 1px;
  border: 0;
  border-top: 1px solid ${props => props.theme.colors.grey200};
  padding: 0;
`;

export const EmptyDescription = styled.div`
  color: ${props => props.theme.colors.grey700};
  font-size: ${props => props.theme.fontSizes[3]};
  font-style: italic;
`;

export const UL = styled.ul`
  margin-left: 0;
  font-size: ${props => props.theme.fontSizes[2]};
  line-height: 24px;
  color: ${props => props.theme.colors.grey700};
`;

export const LI = styled.li`
  list-style-type: none;
  padding-top: ${props => props.theme.global.gutterSpace * 0.5}px;
  line-height: 19px;
  &::before {
    content: '- ';
  }
`;

export const AccordionHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 64px;
  font-size: ${props => props.theme.fontSizes[2]};
  color: ${props => props.theme.colors.grey900};
  cursor: pointer;

  svg {
    transition: transform 200ms ease-out;
    transform: scale(${props => (props.openedAccordion ? 1 : -1)});
  }
`;
